import { Loader2, Star } from 'lucide-react';
import LightningIcon from './icons/LightningIcon';
import ClockIcon from './icons/ClockIcon';
import QuestionGridTooltip from './QuestionGridTooltip';
import api from '../../../services/api';

const ExamCard = ({
    exam,
    language,
    showExamStats,
    activeSessions,
    isActive,
    isFavorite,
    onToggleFavorite,
    openTooltip,
    setOpenTooltip,
    loadingHistoryExamId,
    loadingAnnexeId,
    setLoadingAnnexeId,
    levelId,
    getLocalizedText,
    onExamAction,
    onHistoryClick,
}) => {
    return (
        <div
            className={`bg-white dark:bg-[#121212] rounded-2xl p-4 lg:p-6 border flex flex-col lg:flex-row items-start lg:items-center justify-between group transition-all shadow-sm dark:shadow-none gap-4 lg:gap-0 ${
                exam.requiresUpgrade
                    ? 'opacity-60 grayscale pointer-events-none hover:opacity-80 hover:grayscale-0 border-gray-200 dark:border-white/10'
                    : isActive
                        ? 'border-blue-400 dark:border-blue-500/60 ring-2 ring-blue-400/30 dark:ring-blue-500/20 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
                        : 'border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
            }`}
        >
            <div className="flex-1 w-full lg:w-auto">
                <h4 className="text-base lg:text-lg font-bold text-gray-900 dark:text-white mb-3 lg:mb-4 tracking-tight flex items-center gap-3 flex-wrap">
                    {/* Favorite star */}
                    {onToggleFavorite && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onToggleFavorite(exam.id); }}
                            className="flex-shrink-0 p-0.5 transition-colors"
                            title={isFavorite ? (language === 'fr' ? 'Retirer des favoris' : 'Remove from favorites') : (language === 'fr' ? 'Ajouter aux favoris' : 'Add to favorites')}
                        >
                            <Star size={16} className={isFavorite ? 'text-amber-400 fill-amber-400' : 'text-gray-300 dark:text-gray-600 hover:text-amber-300 dark:hover:text-amber-500'} />
                        </button>
                    )}
                    {/* Lock icon for paid exams */}
                    {exam.requiresUpgrade && (
                        <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    )}
                    {getLocalizedText(exam.title)}
                    {exam.isNew && (
                        <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-red-500 text-white shadow-lg shadow-red-500/30 animate-pulse">
                            {language === 'fr' ? 'Nouveau' : 'New'}
                        </span>
                    )}
                    {/* Warning: active session fully answered but not validated */}
                    {(() => {
                        const session = activeSessions[exam.id];
                        if (!session) return null;
                        const sessionAnswers = session.answers || {};
                        const realAnswers = Object.values(sessionAnswers).filter(a => a !== 0 && a !== '0' && a !== undefined && a !== null).length;
                        const totalQ = exam.questionsCount || 0;
                        if (totalQ === 0 || realAnswers < totalQ) return null;
                        return (
                            <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400"
                                title={language === 'fr' ? 'Session en cours non validée' : 'Unvalidated active session'}
                            >
                                {language === 'fr' ? 'Non validé' : 'Not validated'}
                            </span>
                        );
                    })()}
                    {/* Premium badge for paid exams */}
                    {exam.requiresUpgrade && (
                        <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400">
                            Premium
                        </span>
                    )}
                </h4>
                {showExamStats && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:flex lg:flex-row items-start lg:items-center gap-4 lg:gap-8 w-full">
                    {/* Slot 1: Progression */}
                    {(() => {
                        const session = activeSessions[exam.id];
                        const statuses = exam.questionStatuses || [];
                        const totalQ = statuses.length || exam.questionsCount || 1;
                        let progressVal = exam.progression || 0;
                        let sessionPct = 0;
                        let historicPct = 0;
                        let hasSession = false;

                        if (session) {
                            hasSession = true;
                            const sessionAns = session.answers || {};
                            let sessionCount = 0;
                            let historicOnlyCount = 0;
                            statuses.forEach(qs => {
                                const ans = sessionAns[qs.qId];
                                const inSession = ans !== undefined && ans !== 0 && ans !== '0' && ans !== null;
                                if (inSession) sessionCount++;
                                else if (qs.a) historicOnlyCount++;
                            });
                            sessionPct = Math.round((sessionCount / totalQ) * 100);
                            historicPct = Math.round((historicOnlyCount / totalQ) * 100);
                            progressVal = sessionPct;
                        }

                        return (
                            <QuestionGridTooltip
                                description={hasSession
                                    ? { fr: 'Avancement de la session en cours', en: 'Current session progress' }
                                    : { fr: 'Questions auxquelles vous avez répondu au moins une fois', en: 'Questions you have answered at least once' }
                                }
                                questionStatuses={statuses}
                                type="progression"
                                language={language}
                                className="w-full lg:w-auto lg:min-w-[130px]"
                                isOpen={openTooltip === `${exam.id}-progression`}
                                onToggle={(val) => setOpenTooltip(val ? `${exam.id}-progression` : null)}
                                sessionAnswers={session ? (session.answers || {}) : null}
                            >
                                <div className="flex flex-col gap-1.5">
                                    <div className="flex justify-between items-center w-full gap-2">
                                        <span className="text-[11px] font-black uppercase tracking-widest text-blue-500 dark:text-blue-400">Progression</span>
                                        <span className="text-xs font-black text-gray-900 dark:text-white">{progressVal}%</span>
                                    </div>
                                    <div className="h-1.5 w-full bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden relative">
                                        {hasSession && historicPct > 0 && (
                                            <div className="absolute inset-y-0 left-0 bg-sky-300/60" style={{ width: `${sessionPct + historicPct}%` }}></div>
                                        )}
                                        <div className="h-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)] relative" style={{ width: `${sessionPct > 0 ? sessionPct : (hasSession ? 0 : (exam.progression || 0))}%` }}></div>
                                    </div>
                                </div>
                            </QuestionGridTooltip>
                        );
                    })()}

                    {/* Slot 2: Compréhension */}
                    <QuestionGridTooltip
                        description={{ fr: 'Bonne réponse à chaque fois vs au moins une fois', en: 'Correct every time vs at least once' }}
                        questionStatuses={exam.questionStatuses || []}
                        type="comprehension"
                        language={language}
                        className="w-full lg:w-auto lg:min-w-[130px]"
                        isOpen={openTooltip === `${exam.id}-comprehension`}
                        onToggle={(val) => setOpenTooltip(val ? `${exam.id}-comprehension` : null)}
                    >
                        <div className="flex flex-col gap-1.5">
                            <div className="flex justify-between items-center w-full gap-2">
                                <span className="text-[11px] font-black uppercase tracking-widest text-purple-500 dark:text-purple-400">{language === 'fr' ? 'Compréhension' : 'Comprehension'}</span>
                                <span className="text-xs font-black text-gray-900 dark:text-white">{exam.comprehension || 0}%</span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
                                <div className="h-full bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.5)]" style={{ width: `${exam.comprehension || 0}%` }}></div>
                            </div>
                        </div>
                    </QuestionGridTooltip>

                    {/* Slot 3: Réussite */}
                    <QuestionGridTooltip
                        language={language}
                        className="w-full lg:w-auto lg:min-w-[130px]"
                        isOpen={openTooltip === `${exam.id}-reussite`}
                        onToggle={(val) => setOpenTooltip(val ? `${exam.id}-reussite` : null)}
                        customContent={(exam.successRateTotal ?? 0) > 0 ? (
                            <>
                                <p className="text-[10px] text-gray-300 mb-2 leading-relaxed">
                                    {language === 'fr'
                                        ? 'Nombre total de réponses correctes sur l\'ensemble de vos tentatives pour cet examen.'
                                        : 'Total correct answers across all your attempts for this exam.'}
                                </p>
                                <div className="flex items-baseline gap-2 mb-1">
                                    <span className="text-lg font-black text-white">{exam.successRateCorrect}</span>
                                    <span className="text-[10px] text-gray-400">/</span>
                                    <span className="text-lg font-black text-white">{exam.successRateTotal}</span>
                                    <span className="text-[10px] text-gray-400">{language === 'fr' ? 'réponses' : 'answers'}</span>
                                </div>
                                <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden mb-1">
                                    <div className="h-full bg-amber-500 rounded-full" style={{ width: `${exam.successRate || 0}%` }}></div>
                                </div>
                                <p className="text-[10px] font-bold text-amber-400">{exam.successRate}% {language === 'fr' ? 'de réussite' : 'success rate'}</p>
                            </>
                        ) : null}
                    >
                        <div className="flex flex-col gap-1.5">
                            <div className="flex justify-between items-center w-full gap-2">
                                <span className="text-[11px] font-black uppercase tracking-widest text-amber-500 dark:text-amber-400">{language === 'fr' ? 'Réussite' : 'Success'}</span>
                                <span className="text-xs font-black text-gray-900 dark:text-white">
                                    {exam.successRate || 0}%
                                    {(exam.successRateTotal ?? 0) > 0 && (
                                        <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 ml-1">
                                            {exam.successRateCorrect}/{exam.successRateTotal}
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
                                <div className="h-full bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]" style={{ width: `${exam.successRate || 0}%` }}></div>
                            </div>
                        </div>
                    </QuestionGridTooltip>

                    {/* Slot 4: Dernier Score */}
                    <QuestionGridTooltip
                        description={{ fr: 'Détail de votre dernière tentative', en: 'Details of your last attempt' }}
                        questionStatuses={exam.questionStatuses || []}
                        type="score"
                        language={language}
                        className="w-full lg:w-auto lg:min-w-[130px]"
                        scoreAnswers={exam.lastScoreDetails?.answers}
                        scoreDate={exam.lastScoreDetails?.date}
                        correctAnswers={exam.correctAnswers || {}}
                        isOpen={openTooltip === `${exam.id}-lastScore`}
                        onToggle={(val) => setOpenTooltip(val ? `${exam.id}-lastScore` : null)}
                    >
                        <div className="flex flex-col gap-1.5">
                            <div className="flex justify-between items-center w-full gap-2">
                                <span className="text-[11px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-600">
                                    {language === 'fr' ? 'Dernier Score' : 'Last Score'}
                                </span>
                                <span className="text-xs font-black text-gray-900 dark:text-white">
                                    {exam.lastScore !== null && exam.lastScore !== undefined ? `${exam.lastScore}%` : '-'}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${exam.lastScore !== null && exam.lastScore !== undefined
                                        ? (exam.lastScore >= (exam.minScore || 60) ? 'bg-emerald-500 shadow-[0_0_10px_rgba(52,211,153,0.5)]' : 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]')
                                        : 'bg-transparent'}`}
                                    style={{ width: `${exam.lastScore || 0}%` }}
                                ></div>
                            </div>
                        </div>
                    </QuestionGridTooltip>

                    {/* Slot 5: Meilleur Score */}
                    <QuestionGridTooltip
                        description={{ fr: 'Détail de votre meilleur résultat', en: 'Details of your best result' }}
                        questionStatuses={exam.questionStatuses || []}
                        type="score"
                        language={language}
                        className="w-full lg:w-auto lg:min-w-[130px]"
                        scoreAnswers={exam.bestScoreDetails?.answers}
                        scoreDate={exam.bestScoreDetails?.date}
                        correctAnswers={exam.correctAnswers || {}}
                        isOpen={openTooltip === `${exam.id}-bestScore`}
                        onToggle={(val) => setOpenTooltip(val ? `${exam.id}-bestScore` : null)}
                    >
                        <div className="flex flex-col gap-1.5">
                            <div className="flex justify-between items-center w-full gap-2">
                                <span className="text-[11px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-600">
                                    {language === 'fr' ? 'Meilleur Score' : 'Best Score'}
                                </span>
                                <span className="text-xs font-black text-gray-900 dark:text-white">
                                    {exam.bestScore !== null ? `${exam.bestScore}%` : '-'}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${exam.bestScore !== null
                                        ? (exam.bestScore >= (exam.minScore || 60) ? 'bg-emerald-500 shadow-[0_0_10px_rgba(52,211,153,0.5)]' : 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]')
                                        : 'bg-transparent'}`}
                                    style={{ width: `${exam.bestScore || 0}%` }}
                                ></div>
                            </div>
                        </div>
                    </QuestionGridTooltip>
                </div>
                )}
            </div>
            <div className="flex items-center gap-2 w-full lg:w-auto">
                {/* History button with styled tooltip */}
                <div className="relative group/history">
                    <button
                        onClick={() => onHistoryClick(exam)}
                        disabled={loadingHistoryExamId === exam.id}
                        className="p-2 lg:p-2.5 rounded-xl font-black text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10 transition flex items-center justify-center disabled:opacity-50"
                    >
                        {loadingHistoryExamId === exam.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                        )}
                    </button>
                    {/* Styled tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-[100] w-[200px] opacity-0 scale-95 pointer-events-none group-hover/history:opacity-100 group-hover/history:scale-100 transition-all duration-200 origin-bottom">
                        <div className="bg-gray-900 dark:bg-gray-800 rounded-xl p-3 shadow-2xl border border-white/10">
                            <p className="text-[10px] text-gray-300 leading-relaxed text-center">
                                {language === 'fr'
                                    ? 'Voir les résultats obtenus par question aux différentes sessions'
                                    : 'View results by question across different sessions'}
                            </p>
                        </div>
                        {/* Arrow */}
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 border-r border-b border-white/10" />
                    </div>
                </div>
                {/* Annexe PDF download button */}
                {exam.hasAnnexe && (
                    <div className="relative group/annexe">
                        <button
                            onClick={async () => {
                                if (loadingAnnexeId) return;
                                setLoadingAnnexeId(exam.id);
                                const title = getLocalizedText(exam.title).replace(/[^a-zA-Z0-9]/g, '_');
                                try {
                                    await api.downloadAnnexeBlob(exam.id, `${levelId}_${title}_${exam.id}.pdf`);
                                } catch (e) {
                                    console.error('Annexe download failed:', e);
                                } finally {
                                    setLoadingAnnexeId(null);
                                }
                            }}
                            disabled={loadingAnnexeId === exam.id}
                            className={`p-2 lg:p-2.5 rounded-xl font-black text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10 transition flex items-center justify-center ${loadingAnnexeId === exam.id ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
                        >
                            {loadingAnnexeId === exam.id ? (
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                            ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            )}
                        </button>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-[100] w-[200px] opacity-0 scale-95 pointer-events-none group-hover/annexe:opacity-100 group-hover/annexe:scale-100 transition-all duration-200 origin-bottom">
                            <div className="bg-gray-900 dark:bg-gray-800 rounded-xl p-3 shadow-2xl border border-white/10">
                                <p className="text-[10px] text-gray-300 leading-relaxed text-center">
                                    {language === 'fr'
                                        ? 'Télécharger l\'annexe PDF de cet examen'
                                        : 'Download the PDF appendix for this exam'}
                                </p>
                            </div>
                            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 border-r border-b border-white/10" />
                        </div>
                    </div>
                )}
                {/* Start/Continue/Restart button or Unlock button */}
                <button
                    onClick={() => onExamAction(exam)}
                    className={`px-4 lg:px-6 py-2 lg:py-2.5 rounded-xl font-black text-xs shadow-xl transition hover:scale-105 active:scale-95 flex items-center gap-2 flex-1 lg:flex-none justify-center ${
                        exam.requiresUpgrade
                            ? 'pointer-events-auto text-amber-700 dark:text-amber-300 bg-gradient-to-r from-amber-100 to-amber-200 dark:from-amber-500/20 dark:to-amber-600/20 shadow-amber-500/20 border border-amber-300 dark:border-amber-500/30'
                            : 'text-white bg-blue-600 shadow-blue-500/20'
                    }`}
                >
                    {exam.requiresUpgrade ? (
                        <>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            {language === 'fr' ? 'Débloquer' : 'Unlock'}
                        </>
                    ) : (
                        <>
                            {activeSessions[exam.id] && (
                                activeSessions[exam.id].mode === 'flash'
                                    ? <LightningIcon className="w-4 h-4" />
                                    : <ClockIcon className="w-4 h-4" />
                            )}
                            {activeSessions[exam.id]
                                ? (language === 'fr' ? 'Continuer' : 'Continue')
                                : (exam.lastScore !== null || exam.bestScore !== null)
                                    ? (language === 'fr' ? 'Recommencer' : 'Restart')
                                    : (language === 'fr' ? 'Commencer' : 'Start')}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default ExamCard;
